import styled from '@emotion/styled';
import { ThemeContext } from '@emotion/react';
import { useContext } from 'react';
import { useSize } from 'react-use';

import { AppTheme } from 'theme';

import { Flex } from 'components';
import { SceneWithProgress } from 'pages/Lobby/ScenesProgressProvider';
import Scene, { SceneExtended } from './Scene';

import { GameDifficulty, SceneStatusEnum } from 'graphql/types';
import { SceneStatusFilterAll, SceneStatusFilterEnum } from '../MatchView';

interface Props {
  scenes: SceneWithProgress[];
  selectedSceneId?: string;
  selectedFilter?: SceneStatusFilterEnum;
  gameDifficulty: GameDifficulty;
  showEmptyProgress: boolean;
  handleSceneCLick: (sceneId: string | undefined) => void;
}

const SceneList = ({
  scenes,
  selectedSceneId,
  selectedFilter,
  handleSceneCLick,
  gameDifficulty,
  showEmptyProgress = false,
}: Props) => {
  const { layout } = useContext(ThemeContext) as AppTheme;
  const IS_EDU = !!layout.edu;

  // Edu layout has wider scenes, for better layout we have to calculate with it
  const SCENE_WIDTH = IS_EDU ? 216 : 196;

  const getListMaxWidth = (width: number) => {
    const scenesCount = scenes.length;
    let maxScenesInRow = Math.ceil(width / SCENE_WIDTH);
    let lastRowScenesCount = 0;
    do {
      maxScenesInRow--;
      lastRowScenesCount =
        scenesCount - Math.floor(scenesCount / maxScenesInRow) * maxScenesInRow;
    } while (maxScenesInRow - lastRowScenesCount > 1 && lastRowScenesCount);
    return maxScenesInRow * SCENE_WIDTH;
  };

  const getFilteredScenes = () => {
    if (!selectedFilter || selectedFilter === SceneStatusFilterAll.ALL) {
      return scenes;
    }

    if (selectedFilter === SceneStatusEnum.TRAINING) {
      return scenes.filter((scene) => !!scene.progress);
    }

    return scenes.filter(
      (scene) => (scene.statuses || []).indexOf(selectedFilter) > -1,
    );
  };

  const [sizedScenes] = useSize(
    ({ width }) => (
      <CentredFlex>
        <HorizontalList width={getListMaxWidth(width)}>
          {IS_EDU &&
            getFilteredScenes().map((scene) => (
              <SceneExtended
                key={scene.id}
                scene={scene}
                gameDifficulty={gameDifficulty}
                showEmptyProgress={showEmptyProgress}
                handleClick={handleSceneCLick}
              />
            ))}
          {!IS_EDU &&
            scenes.map((scene) => (
              <Scene
                key={scene.id}
                scene={scene}
                gameDifficulty={gameDifficulty}
                selectedSceneId={selectedSceneId}
                showEmptyProgress={showEmptyProgress}
                handleClick={handleSceneCLick}
              />
            ))}
        </HorizontalList>
      </CentredFlex>
    ),
    { width: 768 },
  );

  return <>{sizedScenes}</>;
};

export default SceneList;

const CentredFlex = styled(Flex)({
  width: '100%',
  justifyContent: 'center',
});

const HorizontalList = styled('div')<{ width: number }>(({ width }) => ({
  display: 'flex',
  justifyContent: 'center',
  color: 'white',
  flexDirection: 'row',
  marginTop: '16px',
  flexWrap: 'wrap',
  maxWidth: width,
}));
